import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transform } from 'sucrase';
import { Pencil, Plus, Trash2, X, FlaskConical } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Select } from '@/components/shared/select';
import { StatusBadge } from '@/components/shared/status-badge';
import type {
  CustomRecognizer,
  CustomRecognizerInput,
  PatternSpec,
  RecognizerValidator,
  RecognizerTestResult,
  ValidatorAlgorithm,
} from '@/lib/types';

const BASE = '/api/admin/custom-recognizers';

function parseList(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

/** Transpile a TS validator source to JS (browser-side, via sucrase). */
function compileTs(src: string): string {
  return transform(src, { transforms: ['typescript'], production: true }).code;
}

export function CustomRecognizers(): JSX.Element {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CustomRecognizer | null>(null);

  const recognizers = useQuery({
    queryKey: [BASE],
    queryFn: () => api.get<CustomRecognizer[]>(BASE),
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: [BASE] });
  };

  const create = useMutation({
    mutationFn: (v: CustomRecognizerInput) => api.post<CustomRecognizer>(BASE, v),
    onSuccess: () => { invalidate(); setShowForm(false); },
  });
  const update = useMutation({
    mutationFn: ({ id, v }: { id: number; v: CustomRecognizerInput }) =>
      api.patch<CustomRecognizer>(`${BASE}/${id}`, v),
    onSuccess: () => { invalidate(); setEditing(null); },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`${BASE}/${id}`),
    onSuccess: invalidate,
  });

  const mutationError = create.error || update.error;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Custom szűrők</h2>
        <button
          className="btn-primary"
          onClick={() => { setEditing(null); setShowForm((v) => !v); }}
        >
          <Plus className="h-4 w-4" /> {showForm ? 'Bezárás' : 'Új szűrő'}
        </button>
      </div>

      {mutationError && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Mentési hiba: {String(mutationError)}
        </div>
      )}

      {(showForm || editing) && (
        <RecognizerForm
          key={editing?.id ?? 'new'}
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(v) => {
            if (editing) update.mutate({ id: editing.id, v });
            else create.mutate(v);
          }}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Név</th>
              <th className="px-4 py-2.5">Placeholder címke</th>
              <th className="px-4 py-2.5">Aktív</th>
              <th className="px-4 py-2.5">Minták</th>
              <th className="px-4 py-2.5">Kontextus</th>
              <th className="px-4 py-2.5">Validátorok</th>
              <th className="w-24 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {recognizers.isLoading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {recognizers.isError && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-rose-600">Hiba: {String(recognizers.error)}</td></tr>
            )}
            {recognizers.data?.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Nincs custom szűrő.</td></tr>
            )}
            {recognizers.data?.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium">{r.name}</td>
                <td className="px-4 py-2.5"><code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">{r.entityLabel}</code></td>
                <td className="px-4 py-2.5">
                  {r.enabled ? <StatusBadge label="aktív" tone="green" /> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-500">{r.patterns.length}</td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">
                  {r.context.join(', ') || '—'}{r.requireContext ? ' (kötelező)' : ''}
                </td>
                <td className="px-4 py-2.5 text-slate-500">
                  {r.validators.length === 0
                    ? '—'
                    : r.validators.map((v) => (v.kind === 'builtin' ? v.algorithm : 'script')).join(', ')}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button title="Szerkeszt" className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => { setShowForm(false); setEditing(r); }}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button title="Töröl" className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => { if (confirm(`Törlés: "${r.name}"?`)) remove.mutate(r.id); }}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// UI-side validator row: builtin keeps params as an editable JSON string.
interface ValidatorRow {
  kind: 'builtin' | 'script';
  algorithm: ValidatorAlgorithm;
  paramsText: string;
  tsSource: string;
}

const ALGO_OPTIONS = [
  { value: 'luhn', label: 'luhn (mod-10)' },
  { value: 'mod11', label: 'mod11 (súlyozott)' },
  { value: 'date_embedded', label: 'date_embedded (dátum-konzisztencia)' },
  { value: 'regex_groups', label: 'regex_groups' },
];

const ALGO_PARAM_HINT: Record<ValidatorAlgorithm, string> = {
  luhn: '{}',
  mod11: '{"weights":[1,2,3,4,5,6,7,8,9,10],"mod":11,"checkIndex":10}',
  date_embedded: '{"offset":1,"length":6,"allowedLeadingDigits":[1,2],"leadingIndex":0}',
  regex_groups: '{"rules":[{"regex":"\\\\d{6}[A-Z]{2}"}]}',
};

const DEFAULT_SCRIPT =
  'function validate(value: string): boolean {\n  // return true to keep the match, false to drop it\n  return value.replace(/\\D/g, "").length === 11;\n}';

function toValidatorRow(v: RecognizerValidator): ValidatorRow {
  if (v.kind === 'builtin') {
    return { kind: 'builtin', algorithm: v.algorithm, paramsText: JSON.stringify(v.params ?? {}, null, 0), tsSource: DEFAULT_SCRIPT };
  }
  return { kind: 'script', algorithm: 'luhn', paramsText: '{}', tsSource: v.tsSource || DEFAULT_SCRIPT };
}

function RecognizerForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: CustomRecognizer | null;
  onSubmit: (v: CustomRecognizerInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const isEdit = Boolean(initial);
  const [name, setName] = useState(initial?.name ?? '');
  const [entityLabel, setEntityLabel] = useState(initial?.entityLabel ?? '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [patterns, setPatterns] = useState<PatternSpec[]>(
    initial?.patterns ?? [{ name: 'p1', regex: '', score: 0.35 }],
  );
  const [context, setContext] = useState((initial?.context ?? []).join(', '));
  const [prefixCount, setPrefixCount] = useState(initial?.contextPrefixCount ?? 5);
  const [suffixCount, setSuffixCount] = useState(initial?.contextSuffixCount ?? 0);
  const [minScore, setMinScore] = useState(initial?.minScoreWithContextSimilarity ?? 0.4);
  const [factor, setFactor] = useState(initial?.contextSimilarityFactor ?? 0.35);
  const [requireContext, setRequireContext] = useState(initial?.requireContext ?? false);
  const [validators, setValidators] = useState<ValidatorRow[]>(
    (initial?.validators ?? []).map(toValidatorRow),
  );
  const [buildError, setBuildError] = useState<string | null>(null);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<RecognizerTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  const updatePattern = (i: number, patch: Partial<PatternSpec>): void =>
    setPatterns((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addPattern = (): void =>
    setPatterns((ps) => [...ps, { name: `p${ps.length + 1}`, regex: '', score: 0.35 }]);
  const removePattern = (i: number): void => setPatterns((ps) => ps.filter((_, idx) => idx !== i));

  const updateValidator = (i: number, patch: Partial<ValidatorRow>): void =>
    setValidators((vs) => vs.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const addValidator = (): void =>
    setValidators((vs) => [...vs, { kind: 'builtin', algorithm: 'luhn', paramsText: '{}', tsSource: DEFAULT_SCRIPT }]);
  const removeValidator = (i: number): void => setValidators((vs) => vs.filter((_, idx) => idx !== i));

  /** Build the API payload, transpiling scripts and parsing params. */
  function buildInput(): CustomRecognizerInput {
    const builtValidators: RecognizerValidator[] = validators.map((v, i) => {
      if (v.kind === 'script') {
        const jsCompiled = compileTs(v.tsSource);
        return { kind: 'script', tsSource: v.tsSource, jsCompiled };
      }
      let params: Record<string, unknown> = {};
      const text = v.paramsText.trim();
      if (text) {
        try {
          params = JSON.parse(text);
        } catch (e) {
          throw new Error(`Validátor #${i + 1} params nem valid JSON: ${String(e)}`);
        }
      }
      return { kind: 'builtin', algorithm: v.algorithm, params };
    });
    return {
      name,
      entityLabel,
      enabled,
      patterns: patterns.filter((p) => p.regex.trim()),
      context: parseList(context),
      contextPrefixCount: prefixCount,
      contextSuffixCount: suffixCount,
      minScoreWithContextSimilarity: minScore,
      contextSimilarityFactor: factor,
      requireContext,
      validators: builtValidators,
    };
  }

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    setBuildError(null);
    try {
      onSubmit(buildInput());
    } catch (err) {
      setBuildError(String(err));
    }
  };

  const runTest = async (): Promise<void> => {
    setBuildError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const spec = buildInput();
      const res = await api.post<RecognizerTestResult>(`${BASE}/test`, {
        spec,
        sampleText: testText,
      });
      setTestResult(res);
    } catch (err) {
      setBuildError(String(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <form className="card space-y-4 p-4" onSubmit={submit}>
      <div className="text-sm font-semibold text-slate-700">
        {isEdit ? `Custom szűrő szerkesztése: ${initial?.name}` : 'Új custom szűrő'}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="label">Név</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label">Placeholder címke (NAGYBETŰ_ALÁHÚZÁS)</label>
          <input
            className="input font-mono"
            value={entityLabel}
            onChange={(e) => setEntityLabel(e.target.value.toUpperCase())}
            placeholder="SZEM_IG_SZAM"
            required
          />
          <p className="mt-1 text-xs text-slate-500">
            A placeholderek így néznek ki: <code>[{entityLabel || 'SZEM_IG_SZAM'}_1]</code>, <code>[{entityLabel || 'SZEM_IG_SZAM'}_2]</code>…
          </p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Aktív
      </label>

      {/* Patterns */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="label">Regex minták</label>
          <button type="button" className="btn-secondary" onClick={addPattern}>
            <Plus className="h-4 w-4" /> Minta
          </button>
        </div>
        <div className="space-y-2">
          {patterns.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input w-28" placeholder="név" value={p.name}
                onChange={(e) => updatePattern(i, { name: e.target.value })} />
              <input className="input grow font-mono text-xs" placeholder="regex" value={p.regex}
                onChange={(e) => updatePattern(i, { regex: e.target.value })} />
              <input className="input w-20" type="number" step="0.05" min="0" max="1" title="alap score"
                value={p.score} onChange={(e) => updatePattern(i, { score: Number(e.target.value) })} />
              <button type="button" className="rounded p-1 text-rose-500 hover:bg-rose-50" onClick={() => removePattern(i)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Context */}
      <div>
        <label className="label">Kontextus-kulcsszavak (vesszővel)</label>
        <input className="input" value={context} onChange={(e) => setContext(e.target.value)}
          placeholder="szig, személyi, igazolvány, okmányszám" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div>
          <label className="label">Prefix szavak</label>
          <input className="input" type="number" min="0" max="50" value={prefixCount}
            onChange={(e) => setPrefixCount(Number(e.target.value))} />
        </div>
        <div>
          <label className="label">Suffix szavak</label>
          <input className="input" type="number" min="0" max="50" value={suffixCount}
            onChange={(e) => setSuffixCount(Number(e.target.value))} />
        </div>
        <div>
          <label className="label">Min. score kontextussal</label>
          <input className="input" type="number" step="0.05" min="0" max="1" value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))} />
        </div>
        <div>
          <label className="label">Context faktor</label>
          <input className="input" type="number" step="0.05" min="0" max="1" value={factor}
            onChange={(e) => setFactor(Number(e.target.value))} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" checked={requireContext} onChange={(e) => setRequireContext(e.target.checked)} />
        Kötelező kontextus (csak akkor talál, ha egy kulcsszó megemelte a score-t)
      </label>

      {/* Validators */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="label">Validátorok (mind igaznak kell lennie)</label>
          <button type="button" className="btn-secondary" onClick={addValidator}>
            <Plus className="h-4 w-4" /> Validátor
          </button>
        </div>
        <div className="space-y-3">
          {validators.map((v, i) => (
            <div key={i} className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center gap-2">
                <div className="w-44">
                  <Select
                    value={v.kind}
                    onChange={(val) => updateValidator(i, { kind: val as 'builtin' | 'script' })}
                    options={[
                      { value: 'builtin', label: 'Beépített algoritmus' },
                      { value: 'script', label: 'Script (TS/JS)' },
                    ]}
                  />
                </div>
                {v.kind === 'builtin' && (
                  <div className="w-72">
                    <Select
                      value={v.algorithm}
                      onChange={(val) => updateValidator(i, { algorithm: val as ValidatorAlgorithm, paramsText: ALGO_PARAM_HINT[val as ValidatorAlgorithm] })}
                      options={ALGO_OPTIONS}
                    />
                  </div>
                )}
                <button type="button" className="ml-auto rounded p-1 text-rose-500 hover:bg-rose-50" onClick={() => removeValidator(i)}>
                  <X className="h-4 w-4" />
                </button>
              </div>
              {v.kind === 'builtin' ? (
                <div>
                  <label className="label text-xs">Paraméterek (JSON)</label>
                  <input className="input font-mono text-xs" value={v.paramsText}
                    placeholder={ALGO_PARAM_HINT[v.algorithm]}
                    onChange={(e) => updateValidator(i, { paramsText: e.target.value })} />
                </div>
              ) : (
                <div>
                  <label className="label text-xs">TypeScript — <code>validate(value): boolean</code></label>
                  <textarea className="input h-28 w-full font-mono text-xs" value={v.tsSource}
                    onChange={(e) => updateValidator(i, { tsSource: e.target.value })} />
                  <p className="mt-1 text-xs text-slate-500">
                    Izolált sandboxban fut (nincs I/O, idő- és memórialimit). Mentéskor TS→JS fordítás.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {buildError && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{buildError}</div>
      )}

      {/* Live test */}
      <div className="rounded-md border border-border bg-slate-50/50 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <FlaskConical className="h-4 w-4" /> Teszt mintaszövegen
        </div>
        <textarea className="input h-20 w-full text-sm" value={testText}
          onChange={(e) => setTestText(e.target.value)}
          placeholder="Pl.: A személyi igazolvány száma 123456AB." />
        <div className="mt-2 flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={runTest} disabled={testing}>
            {testing ? 'Tesztelés…' : 'Teszt futtatása'}
          </button>
        </div>
        {testResult && (
          <div className="mt-2 text-sm">
            {testResult.error ? (
              <span className="text-rose-600">{testResult.error}</span>
            ) : testResult.matches.length === 0 ? (
              <span className="text-slate-500">Nincs találat.</span>
            ) : (
              <ul className="space-y-1">
                {testResult.matches.map((m, idx) => (
                  <li key={idx} className="font-mono text-xs">
                    <code className="rounded bg-emerald-100 px-1 text-emerald-800">{m.entityType}</code>{' '}
                    "{m.text}" · score {m.score} · {m.source}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>Mégse</button>
        <button type="submit" className="btn-primary"><Plus className="h-4 w-4" /> Mentés</button>
      </div>
    </form>
  );
}
